import React from "react";

const shellStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background:
    "radial-gradient(circle at 18% 12%, rgba(56, 189, 248, 0.16), transparent 24%), radial-gradient(circle at 84% 18%, rgba(139, 92, 246, 0.14), transparent 22%), linear-gradient(180deg, #081320 0%, #0f172a 56%, #08111d 100%)",
};

const cardStyle = {
  width: "100%",
  maxWidth: "560px",
  borderRadius: "24px",
  padding: "32px 28px",
  background: "rgba(15, 23, 42, 0.92)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  boxShadow: "0 28px 90px rgba(2, 6, 23, 0.45)",
  color: "#f8fafc",
  textAlign: "center",
};

const eyebrowStyle = {
  margin: 0,
  color: "#38bdf8",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
};

const titleStyle = {
  margin: "14px 0 0",
  fontSize: "32px",
  lineHeight: 1.1,
  fontWeight: 800,
};

const bodyStyle = {
  margin: "16px 0 0",
  color: "#cbd5e1",
  fontSize: "16px",
  lineHeight: 1.7,
};

const buttonStyle = {
  marginTop: "24px",
  minWidth: "200px",
  height: "48px",
  border: "none",
  borderRadius: "999px",
  background: "linear-gradient(135deg, #38bdf8, #8b5cf6)",
  color: "#ffffff",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 18px 36px rgba(59, 130, 246, 0.28)",
};

const detailStyle = {
  marginTop: "14px",
  color: "#94a3b8",
  fontSize: "13px",
  lineHeight: 1.6,
  wordBreak: "break-word",
};

function normalizeErrorMessage(error) {
  if (!error) return "Unexpected application error";
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Unexpected application error";
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[AppErrorBoundary] Runtime error:", error, errorInfo);
  }

  componentDidMount() {
    window.addEventListener("error", this.handleWindowError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleWindowError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  handleWindowError = (event) => {
    const nextError =
      event?.error ||
      new Error(event?.message || "Unexpected application error");

    this.setState((currentState) =>
      currentState.error ? currentState : { error: nextError },
    );
  };

  handleUnhandledRejection = (event) => {
    const nextError =
      event?.reason instanceof Error
        ? event.reason
        : new Error(
            typeof event?.reason === "string" && event.reason.trim()
              ? event.reason
              : "Unexpected application error",
          );

    this.setState((currentState) =>
      currentState.error ? currentState : { error: nextError },
    );
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const errorMessage = normalizeErrorMessage(this.state.error);

      return (
        <div style={shellStyle}>
          <div role="alert" style={cardStyle}>
            <p style={eyebrowStyle}>Something Went Wrong</p>
            <h1 style={titleStyle}>Please reload the page</h1>
            <p style={bodyStyle}>
              The app hit a runtime error and stopped rendering safely. Reload the
              page to continue.
            </p>
            <button type="button" style={buttonStyle} onClick={this.handleReload}>
              Reload Page
            </button>
            <div style={detailStyle}>{errorMessage}</div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
